import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Bank } from '../../types';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';

interface BankFormProps {
  bank?: Bank;
  onSubmit: (data: Partial<Bank>) => void;
  onCancel: () => void;
}

const BankForm: React.FC<BankFormProps> = ({ bank, onSubmit, onCancel }) => {
  // Fetch currencies from API
  const { data: currencies } = useCurrencies();

  // Saved currency may be a populated object, a documentId, or a legacy code
  // like "USD"; the select option values are documentIds
  const savedCurrencyId = bank ? resolveCurrencyDocumentId(bank.currency, currencies) : '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<any>({
    defaultValues: bank ? {
      ...bank,
      currency: savedCurrencyId,
      isActive: bank.isActive ? 'true' : 'false' // Convert boolean to string for select
    } : {
      name: '',
      accountNumber: '',
      accountType: 'checking',
      balance: 0,
      currency: '',
      isActive: 'true'
    }
  });

  // Re-apply the saved currency once the async options are loaded (edit case)
  useEffect(() => {
    if (savedCurrencyId && currencies && currencies.length > 0) {
      setValue('currency', savedCurrencyId);
    }
  }, [savedCurrencyId, currencies, setValue]);

  const handleFormSubmit = (data: Bank) => {
    onSubmit({
      ...data,
      ...(bank ? { id: bank.id, documentId: bank.documentId } : {}),
      createdAt: bank?.createdAt || new Date().toISOString()
    });
  };

  const accountTypeOptions = [
    { value: 'checking', label: 'Checking Account' },
    { value: 'savings', label: 'Savings Account' },
    { value: 'business', label: 'Business Account' },
    { value: 'foreign', label: 'Foreign Currency Account' }
  ];

  // Currency options from API - NO HARDCODED VALUES
  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) {
      return [];
    }
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`
      }));
  }, [currencies]);

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {bank ? 'Edit Bank Account' : 'Add New Bank Account'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Bank Name"
            {...register('name', {
              required: 'Bank name is required',
              minLength: { value: 2, message: 'Bank name must be at least 2 characters' }
            })}
            error={errors.name?.message as string}
            placeholder="e.g., Marine National Bank"
          />

          <Input
            label="Account Number"
            {...register('accountNumber', {
              required: 'Account number is required'
            })}
            error={errors.accountNumber?.message as string}
            placeholder="e.g., 1234567890"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Account Type"
            {...register('accountType', { required: 'Account type is required' })}
            options={accountTypeOptions}
            error={errors.accountType?.message as string}
          />
          
          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            placeholder="Select currency"
            error={errors.currency?.message as string}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Initial Balance"
            type="number"
            step="0.01"
            {...register('balance', { 
              required: 'Balance is required',
              min: { value: 0, message: 'Balance cannot be negative' }
            })}
            error={errors.balance?.message as string}
            placeholder="0.00"
          />

          <Select
            label="Status"
            {...register('isActive', { 
              required: 'Status is required',
              setValueAs: (value: string) => value === 'true'
            })}
            options={statusOptions}
            error={errors.isActive?.message as string}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {bank ? 'Update Bank Account' : 'Create Bank Account'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BankForm;